import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  PcCase,
  Monitor,
  AppWindow,
  Cpu,
  Users,
  Settings,
  Activity,
  Search,
  X,
  Loader2,
  Menu as MenuIcon, // menú
  FileText, // PDF
  AlertCircle, //  banners
  Download, //  banners
  RefreshCw, //  banners
} from "lucide-react";
import config from "./config"; // Importamos la configuración

interface MenuItemProps {
  text: string;
  selected: boolean;
  onClick: () => void;
  indented?: boolean;
}

interface SystemInfo {
  cpu?: {
    numOfProcessors: number;
    archName: string;
    modelName: string;
    features: string[];
    processors: Array<{
      usage: {
        idle: number;
        kernel: number;
        user: number;
        total: number;
      };
      temperatures: number[];
    }>;
    frequency?: {
      current: number;
      min: number;
      max: number;
    };
    cache?: {
      l1d?: number;
      l1i?: number;
      l2?: number;
      l3?: number;
    };
    flags?: string[];
    temperature?: number;
    core_temps?: number[];
    usage?: number;
    cores?: number;
    threads?: number;
  };
  memory?: {
    capacity: number;
    availableCapacity: number;
    usedPercentage: number;
    type?: string;
    modules?: Array<{
      size: number;
      speed?: number;
      manufacturer?: string;
      location: string;
      memory_type?: string;
      form_factor?: string;
    }>;
  };
  storage?: Array<{
    name: string;
    capacity?: number;
    available?: number;
    type: string;
    type_name: string;
    drive_letter: string;
  }>;

  display?: Array<{
    name?: string;
    primary: boolean;
    resolution: {
      width: number;
      height: number;
    };
    refresh_rate?: number;
    id?: string;
    isEnabled?: boolean;
    isInternal?: boolean;
    dpiX: number;
    dpiY: number;
    rotation?: number;
    colorDepth?: number;
  }>;
  gpu_info?: Array<{
    model: string;
    memory: number;
    driver_version?: string;
    temperature?: number;
    usage?: number;
  }>;
  network_info?: {
    interfaces: Array<{
      name: string;
      rx_bytes: number;
      tx_bytes: number;
      status: string;
    }>;
    total_rx_bytes: number;
    total_tx_bytes: number;
  };
  peripherals?: {
    input_devices: Array<{
      name: string;
      type: string;
      manufacturer?: string;
      connected: boolean;
      interface: string;
    }>;
    audio_devices: Array<{
      name: string;
      type: "playback" | "recording";
      default: boolean;
      active: boolean;
    }>;
    displays: Array<{
      name: string;
      primary: boolean;
      resolution: {
        width: number;
        height: number;
      };
      refresh_rate?: number;
      connection_type?: string;
    }>;
    printers: Array<{
      name: string;
      status: string;
      is_default: boolean;
      is_shared: boolean;
      location?: string;
      manufacturer?: string;
      model?: string;
      color: boolean;
      duplex: boolean;
    }>;
  };
  os_info?: {
    name: string;
    version: string;
    build: string;
    architecture: string;
    kernel_version?: string;
    uptime: number;
    install_date?: string;
    last_boot?: string;
    registered_owner?: string;
    system_root?: string;
    system_drive?: string;
  };
  processes?: {
    total: number;
    threads: number;
    cpu_intensive: Array<{
      name: string;
      pid: number;
      cpu_usage: number;
      memory_usage: number;
    }>;
    memory_intensive: Array<{
      name: string;
      pid: number;
      cpu_usage: number;
      memory_usage: number;
    }>;
  };
  services?: {
    total: number;
    running: number;
    stopped: number;
  };
  nativeAgent?: {
    version: string | null;
    connected: boolean;
  };
  firmware_info?: {
    is_uefi: boolean;
    secure_boot_enabled?: boolean;
    tpm_version?: string;
    tpm_available: boolean;
    direct_x_version?: string;
  };
}

interface UpdateNotification {
  show: boolean;
  currentVersion: string;
  latestVersion: string;
}

type TabType = "system" | "peripherals" | "os" | "vitals";

const getUsageClass = (usage: number) => usage > 90 ? "text-red-500" : "";
const getBarClass = (usage: number) => usage > 90 ? "bg-red-500" : "bg-blue-500";

// Variable global para cachear la información de GPU detectada por WebGL
let cachedWebGLGPUInfo: { model: string; memory: number }[] | null = null;

// Detector de GPU usando WebGL cuando no está disponible el agente nativo
class GraphicsCardDetector {
  renderer: string;
  canvas: HTMLCanvasElement | null;
  context: WebGLRenderingContext | null;

  constructor() {
    this.renderer = "";
    this.canvas = null;
    this.context = null;
    this.initialize();
  }

  initialize() {
    try {
      // Crear un canvas temporal para obtener el contexto WebGL
      this.canvas = document.createElement('canvas');
      // Especificar el tipo de contexto correctamente
      this.context = this.canvas.getContext('webgl') ||
        this.canvas.getContext('experimental-webgl') as WebGLRenderingContext | null;

      if (this.context) {
        // Obtener información del hardware gráfico usando la extensión debug_renderer_info
        const debugExtension = this.context.getExtension('WEBGL_debug_renderer_info') as {
          UNMASKED_VENDOR_WEBGL: number;
          UNMASKED_RENDERER_WEBGL: number;
        } | null;

        if (debugExtension) {
          // Guardar el nombre completo del renderer
          this.renderer = this.context.getParameter(debugExtension.UNMASKED_RENDERER_WEBGL);
        }
      }
    } catch (error) {
      console.error("Error detecting graphics hardware:", error);
    }
  }

  getGraphicsCardName() {
    if (!this.renderer) return "";

    let gpuName = this.renderer;
    // Eliminar referencias a ANGLE (casi siempre presente en Chrome)
    if (gpuName.indexOf("ANGLE (") >= 0) {
      gpuName = gpuName.slice(7, -1);
    }

    // Eliminar referencias a APIs gráficas para obtener solo el modelo
    const apiReferences = [" Direct3D", " OpenGL", " Vulkan"];

    for (const api of apiReferences) {
      const apiIndex = gpuName.indexOf(api);
      if (apiIndex >= 0) {
        gpuName = gpuName.slice(0, apiIndex);
      }
    }

    // Filtrar renderizadores por software
    const softwareRenderers = ["Google SwiftShader", "llvmpipe", "Microsoft Basic Render"];
    if (softwareRenderers.some(renderer => gpuName.includes(renderer))) {
      return "";
    }

    return gpuName;
  }

  // liberar recursos
  dispose() {
    if (this.context) {
      const loseExt = this.context.getExtension('WEBGL_lose_context');
      if (loseExt) {
        loseExt.loseContext();
      }
    }
    this.context = null;
    this.canvas = null;
  }
}

// Obtener información de GPU usando WebGL
function getWebGLGPUInfo() {
  // Si ya tenemos información cacheada, la devolvemos directamente
  if (cachedWebGLGPUInfo !== null) {
    return cachedWebGLGPUInfo;
  }

  const gpuDetector = new GraphicsCardDetector();
  const gpuName = gpuDetector.getGraphicsCardName();

  // Liberar recursos WebGL después de obtener la información
  gpuDetector.dispose();

  if (!gpuName) {
    cachedWebGLGPUInfo = null;
    return null;
  }

  // Crear un objeto similar al que devolvería el agente nativo y guardarlo en caché
  cachedWebGLGPUInfo = [{
    model: gpuName,
    memory: 0 // No podemos obtener memoria dedicada con WebGL
  }];

  return cachedWebGLGPUInfo;
}

// Función para determinar el navegador actual
const getExtensionLink = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  if (userAgent.indexOf('firefox') > -1) {
    return "https://addons.mozilla.org/firefox/addon/pchardware-info";
  } else if (userAgent.indexOf('edg') > -1) {
    return "https://microsoftedge.microsoft.com/addons/detail/my-pchardwareinfo/okjcpocfffkocpkppnkpdndmnbiebodj";
  } else {
    // Chrome por defecto u otros navegadores basados en Chromium
    return "https://chromewebstore.google.com/detail/my-pchardwareinfo/offagpomkenflpdfbepgcjpikpbmfjnk";
  }
};

const getBrowserName = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  if (userAgent.indexOf('firefox') > -1) {
    return "Firefox";
  } else if (userAgent.indexOf('edg') > -1) {
    return "Edge";
  } else {
    return "Chrome";
  }
};

// Extraer el componente MenuItem a la parte superior para reutilizarlo
const MenuItem: React.FC<MenuItemProps> = ({
  text,
  selected,
  onClick,
  indented = false,
}) => (
  <div
    role="button"
    tabIndex={0}
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    onKeyPress={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onClick();
      }
    }}
    className={`
      cursor-pointer 
      py-1 
      ${indented ? "pl-8" : "pl-4"} 
      ${selected ? "bg-gray-600" : "hover:bg-gray-600"}
      transition-colors
      duration-150
      hover:ring-1
      hover:ring-blue-500
      hover:ring-offset-0
      focus:ring-0
      mt-1
      mb-0.4
    `}
  >
    <span className="text-sm">{text}</span>
  </div>
);

// Componente memoizado para el menú lateral
interface SideMenuProps {
  selectedMenuItem: string;
  onMenuItemClick: (menuItem: string) => void;
}

const SideMenu = React.memo(({ selectedMenuItem, onMenuItemClick }: SideMenuProps) => {
  return (
    <div
      className="w-full sm:w-40 bg-gray-700 rounded flex-shrink-0"
      role="navigation"
      aria-label="System navigation"
    >
      <MenuItem
        text="Summary"
        selected={selectedMenuItem === "summary"}
        onClick={() => onMenuItemClick("summary")}
      />
      <MenuItem
        text="CPU"
        selected={selectedMenuItem === "cpu"}
        onClick={() => onMenuItemClick("cpu")}
      />
      <MenuItem
        text="Memory"
        selected={selectedMenuItem === "memory"}
        onClick={() => onMenuItemClick("memory")}
      />
      <MenuItem
        text="GPU"
        selected={selectedMenuItem === "gpu"}
        onClick={() => onMenuItemClick("gpu")}
      />
      <MenuItem
        text="Storage"
        selected={selectedMenuItem === "disk"}
        onClick={() => onMenuItemClick("disk")}
      />
      <MenuItem
        text="Network"
        selected={selectedMenuItem === "network"}
        onClick={() => onMenuItemClick("network")}
      />
    </div>
  );
});

// Asegurar que el componente tiene un nombre para las React DevTools
SideMenu.displayName = 'SideMenu';

// Componente memoizado para el menú de hamburguesa
interface MenuPanelProps {
  buttonRect: DOMRect | undefined;
  onGeneratePDF: () => void;
  onClose: () => void;
  menuRef: React.RefObject<HTMLDivElement>;
}

const MemoizedMenuPanel = React.memo(({
  buttonRect,
  onGeneratePDF,
  onClose,
  menuRef
}: MenuPanelProps) => {
  return (
    <div
      ref={menuRef}
      className="absolute z-50 bg-gray-700 rounded shadow-lg overflow-hidden w-56"
      style={{
        top: buttonRect ? buttonRect.bottom + window.scrollY : '16px',
        left: buttonRect ?
          (buttonRect.left + (buttonRect.width / 2) - 56) + window.scrollX : 'auto',
        transform: 'translateX(-50%)',
        right: 'auto',
      }}
    >
      <div className="p-2">
        <button
          onClick={onGeneratePDF}
          className="w-full text-left px-3 py-2 hover:bg-gray-600 rounded flex items-center gap-2 transition-colors"
        >
          <FileText className="w-5 h-5" />
          <span>Generate PDF report</span>
        </button>
        <a
          href="https://pchardware.info/faq"
          target="_blank"
          rel="noopener noreferrer"
          onClick={onClose}
          className="w-full text-left px-3 py-2 hover:bg-gray-600 rounded flex items-center gap-2 transition-colors mt-1"
        >
          <AlertCircle className="w-5 h-5" />
          <span>FAQ</span>
        </a>
      </div>
    </div>
  );
});

// Asegurar que el componente tiene un nombre para las React DevTools
MemoizedMenuPanel.displayName = 'MemoizedMenuPanel';

const SystemMonitor: React.FC = () => {
  // Añadir estado para el estado de carga inicial
  const [isLoading, setIsLoading] = useState(true);
  const [showExtensionBanner, setShowExtensionBanner] = useState(false);
  const [showRustBanner, setShowRustBanner] = useState(false);
  // Añadir estados para rastrear si el usuario ha cerrado los banners manualmente
  const [extensionBannerClosed, setExtensionBannerClosed] = useState(false);
  const [rustBannerClosed, setRustBannerClosed] = useState(false);
  const [updateBannerClosed, setUpdateBannerClosed] = useState(false);
  const [updateNotification, setUpdateNotification] = useState<UpdateNotification>({
    show: false,
    currentVersion: "",
    latestVersion: ""
  });
  const [agentReady, setAgentReady] = useState(false); // Nuevo estado para el agente nativo
  // Nuevo estado para controlar el progreso de la barra
  const [progressValue, setProgressValue] = useState(0);

  // Modificar el estado inicial para que coincida con la vista por defecto
  const [activeTab, setActiveTab] = useState<TabType>("system");
  const [selectedMenuItem, setSelectedMenuItem] = useState("summary");
  const [cpuData, setCpuData] = useState<number[][]>(Array(8).fill([]));
  const [searchTerm, setSearchTerm] = useState("");
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [notifications, setNotifications] = useState<string[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [sortConfig, setSortConfig] = useState({
    key: "cpu",
    direction: "desc",
  });
  //  showMenu
  const [showMenu, setShowMenu] = useState(false);
  const [systemInfo, setSystemInfo] = useState<SystemInfo>({});
  // Agregar un ref para el menú para cerrar al hacer click fuera
  const menuRef = useRef<HTMLDivElement>(null);
  // Agregar un ref para el botón del menú
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  // Modificación de la determinación del disco del sistema
  const systemDrive = systemInfo.os_info?.system_drive;
  const isWindows = systemInfo.os_info?.name?.toLowerCase().includes('windows');

  // Encontrar el disco del sistema 
  const systemDisk = React.useMemo(() => {
    if (!systemInfo.storage || systemInfo.storage.length === 0) {
      return null;
    }

    // Si solo hay un disco, asumimos que es el del sistema
    if (systemInfo.storage.length === 1) {
      return systemInfo.storage[0];
    } else if (isWindows && systemDrive) {
      // Limpiar la letra de unidad para la comparación (eliminar posible ":")
      const driveLetter = systemDrive.replace(/[:\\/]/g, '').toUpperCase();

      // En Windows, buscar el disco que coincide con system_drive
      const matchingDisk = systemInfo.storage.find(d => {
        // Normalizar la letra de unidad del disco
        const diskDriveLetter = d.drive_letter?.replace(/[:\\/]/g, '').toUpperCase();
        // console.log(`Comparando: Drive=${driveLetter}, Disco=${diskDriveLetter}`);
        return diskDriveLetter === driveLetter;
      });

      if (matchingDisk) {
        return matchingDisk;
      }
    } else if (!isWindows) {
      // En sistemas no-Windows (Linux/Mac), buscar el disco raíz "/"
      const rootDisk = systemInfo.storage.find(d => d.name === "/");
      if (rootDisk) {
        return rootDisk;
      }
    }

    // Si no podemos determinar con seguridad cuál es el disco del sistema, no asumimos nada
    return null;
  }, [systemInfo.storage, isWindows, systemDrive]);

  // Calcular el uso del disco del sistema
  const systemDiskUsage = React.useMemo(() => {
    if (!systemDisk) {
      return 0;
    }

    if (!systemDisk.capacity || !systemDisk.available || systemDisk.capacity <= 0) {
      return 0;
    }

    const usage = ((systemDisk.capacity - systemDisk.available) / systemDisk.capacity) * 100;
    return usage;
  }, [systemDisk]);

  // Efecto para datos del sistema
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === "SYSTEM_INFO_RESPONSE") {

        if (event.data.data.error) {
          console.warn("❌ Extension error:", event.data.data.error);
          console.warn("Please, reload page...");
          return;
        }
        // Log detallado para depuración
        const dataReceived = { ...event.data.data };

        // Si no hay información de GPU del agente nativo, intentar obtenerla con WebGL
        if (!dataReceived.gpu_info || dataReceived.gpu_info.length === 0) {
          const webGLGPUInfo = getWebGLGPUInfo();
          // console.log("🖥️ Using WebGL for GPU info:", webGLGPUInfo);
          if (webGLGPUInfo) {
            dataReceived.gpu_info = webGLGPUInfo;
          }
        } else {
          // console.log("🖥️ GPU data from extension:", dataReceived.gpu_info);
        }

        setSystemInfo(dataReceived);

        // Comprobar si hay actualización disponible
        if (event.data.data.nativeAgent?.version && event.data.data.nativeAgent.latestVersion) {
          const current = event.data.data.nativeAgent.version;
          const latest = event.data.data.nativeAgent.latestVersion;
          console.log("🔄 Agent versions - Current:", current, "Latest:", latest);
          if (current !== latest) {
            setUpdateNotification({
              show: true,
              currentVersion: current,
              latestVersion: latest
            });
          }
        }
      }
    };

    window.addEventListener("message", handleMessage);

    // Solicitar información inicial
    window.postMessage({ type: "REQUEST_SYSTEM_INFO" }, "*");

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []); // Solo se ejecuta una vez al montar el componente

  // Efecto para comprobar si la extensión está instalada
  useEffect(() => {
    const checkExtensionAndRust = () => {
      let extensionResponded = false;
      let rustResponded = false;
      let extensionTimeoutId: NodeJS.Timeout;
      let rustTimeoutId: NodeJS.Timeout;

      const handleResponse = (event: MessageEvent) => {
        if (event.data.type === "SYSTEM_INFO_RESPONSE") {
          extensionResponded = true;
          setIsLoading(false);
          clearTimeout(extensionTimeoutId);

          // Si la extensión responde, no mostrar el banner de la extensión
          // Solo mostrar el banner si no ha sido cerrado por el usuario
          if (!extensionBannerClosed) {
            setShowExtensionBanner(false);
          }

          // Hacer una copia de los datos recibidos
          const dataReceived = { ...event.data.data };

          // Si no hay información de GPU, intentar obtenerla con WebGL
          if (!dataReceived.gpu_info || dataReceived.gpu_info.length === 0) {
            const webGLGPUInfo = getWebGLGPUInfo();
            if (webGLGPUInfo) {
              dataReceived.gpu_info = webGLGPUInfo;
            }
          }

          //console.log("version: ", dataReceived.nativeAgent?.version);
          if (dataReceived.nativeAgent?.version) {
            rustResponded = true;
            // Si el agente nativo responde, cancelar el timeout y ocultar el banner
            clearTimeout(rustTimeoutId);
            // Solo mostrar el banner si no ha sido cerrado por el usuario
            if (!rustBannerClosed) {
              setShowRustBanner(false);
            }
            setAgentReady(true); // Se marca que el agente está listo
            //console.log("Helper detected and truly connected with real data");
          } else {
            // No mostramos el banner aquí, se manejará con el timeout
            // console.log("Helper not detected or not connected, waiting to show banner");
          }

          // Actualizar la información del sistema con los datos enriquecidos
          setSystemInfo(dataReceived);
        }
      };

      // Configurar un timeout para detectar si la extensión no está instalada (2 segundos)
      extensionTimeoutId = setTimeout(() => {
        if (!extensionResponded) {
          // Solo mostrar los banners si no han sido cerrados por el usuario
          if (!extensionBannerClosed) {
            setShowExtensionBanner(true);
          }
          // Si la extensión no está instalada, mostrar también el banner del agente nativo inmediatamente
          if (!rustBannerClosed) {
            setShowRustBanner(true);
          }
          setIsLoading(false);
        }
      }, 2000);

      // Configurar un timeout específico para el banner del agente nativo (10 segundos)
      // Solo se activará si la extensión está instalada pero el agente nativo no responde
      rustTimeoutId = setTimeout(() => {
        if (extensionResponded && !rustResponded && !rustBannerClosed) {
          setShowRustBanner(true);
          // console.log("Showing Rust banner after 10 seconds timeout");
        }
      }, 10000); // Esperar 10 segundos antes de mostrar el banner del agente nativo

      window.addEventListener("message", handleResponse);
      window.postMessage({ type: "REQUEST_SYSTEM_INFO" }, "*");

      return () => {
        window.removeEventListener("message", handleResponse);
        clearTimeout(extensionTimeoutId);
        clearTimeout(rustTimeoutId);
      };
    };

    checkExtensionAndRust();
    // REFRESH SCREEN EVERY 1 SECOND
    const interval = setInterval(checkExtensionAndRust, 1000);

    return () => clearInterval(interval);
  }, [extensionBannerClosed, rustBannerClosed]);

  // Efecto para incrementar gradualmente la barra de progreso cuando estamos esperando al helper
  useEffect(() => {
    let interval: NodeJS.Timeout;
    let startTime: number | null = null;
    const totalDuration = 10000; // Duración total: 10 segundos (igual que el timeout del banner)

    // Solo incrementar el progreso si estamos esperando que el helper responda
    if (!isLoading && !agentReady && !showRustBanner) {
      startTime = Date.now();

      interval = setInterval(() => {
        const elapsedTime = Date.now() - (startTime || 0);
        const newProgressValue = Math.min(100, (elapsedTime / totalDuration) * 100);

        setProgressValue(newProgressValue);

        // Si llegamos al 100%, detenemos el intervalo
        if (newProgressValue >= 100) {
          clearInterval(interval);
        }
      }, 50); // Actualizar más frecuentemente para una animación más suave
    } else {
      // Resetear el progreso cuando ya no estamos esperando
      setProgressValue(0);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isLoading, agentReady, showRustBanner]);

  // Modificar la función que maneja los clicks del menú lateral
  const handleMenuItemClick = useCallback((menuItem: string) => {
    setSelectedMenuItem(menuItem);
    setActiveTab("system");
  }, []);

  // Modificar la función para manejar los clicks del menú superior
  const handleTabClick = (tab: TabType) => {
    setActiveTab(tab);

    if (tab === "system") {
      // Al volver a system, si no hay selección, poner summary
      if (!selectedMenuItem) {
        setSelectedMenuItem("summary");
      }
    }
  };

  // Modificar la función toggleMenu a toggleMenu
  const toggleMenu = useCallback(() => {
    setShowMenu(!showMenu);
  }, [showMenu]);

  // Agregar una función para generar PDF usando useCallback
  const generatePDF = useCallback(() => {
    alert('Option not available yet');
    setShowMenu(false); // Cerrar el menú al seleccionar
  }, []);

  // Callback para cerrar el menú
  const closeMenu = useCallback(() => {
    setShowMenu(false);
  }, []);

  // Añadir un efecto para cerrar el menú al hacer clic fuera
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuRef]);

  const TabContent = () => {
    // CPU Information render function
    const renderCPUInfo = () => (
      <div className="bg-gray-700 p-4 rounded">
        <h2 className="text-xl mb-4">CPU Information</h2>
        <div className="bg-gray-800 p-4 rounded">
          <div className="text-sm space-y-3">
            <div>
              Model:{" "}
              {systemInfo.cpu?.modelName || (
                <span className="text-red-500">Not available</span>
              )}
            </div>
            <div>
              Architecture:{" "}
              {systemInfo.cpu?.archName || (
                <span className="text-red-500">Not available</span>
              )}
            </div>
            <div>
              Physical cores:{" "}
              {systemInfo.cpu?.cores || systemInfo.cpu?.numOfProcessors || (
                <span className="text-red-500">Not available</span>
              )}
            </div>
            <div>
              Threads:{" "}
              {systemInfo.cpu?.threads || (
                <span className="text-red-500">Not available</span>
              )}
            </div>
            <div>
              Base frequency:{" "}
              {systemInfo.cpu?.frequency?.current ? (
                `${systemInfo.cpu.frequency.current.toFixed(2)} MHz`
              ) : (
                <span className="text-red-500">Not available</span>
              )}
            </div>
            <div>
              Max frequency:{" "}
              {systemInfo.cpu?.frequency?.max ? (
                `${systemInfo.cpu.frequency.max.toFixed(2)} MHz`
              ) : (
                <span className="text-red-500">Not available</span>
              )}
            </div>
            <div>
              L1 Cache:{" "}
              {systemInfo.cpu?.cache?.l1d ? (
                `${systemInfo.cpu.cache.l1d} KB (Data) / ${systemInfo.cpu.cache.l1i || 0
                } KB (Instruction)`
              ) : (
                <span className="text-red-500">Not available</span>
              )}
            </div>
            <div>
              L2 Cache:{" "}
              {systemInfo.cpu?.cache?.l2 ? (
                `${systemInfo.cpu.cache.l2} KB`
              ) : (
                <span className="text-red-500">Not available</span>
              )}
            </div>
            <div>
              L3 Cache:{" "}
              {systemInfo.cpu?.cache?.l3 ? (
                `${systemInfo.cpu.cache.l3} KB`
              ) : (
                <span className="text-red-500">Not available</span>
              )}
            </div>
            {/* 
            <div>
              Temperature: {systemInfo.cpu?.temperature ? 
                `${systemInfo.cpu.temperature.toFixed(1)}°C` : 
                <span className="text-red-500">Not available</span>}
            </div>
            
            {systemInfo.cpu?.core_temps && systemInfo.cpu.core_temps.length > 0 && (
              <div className="mt-2">
                <div className="font-medium mb-1">Core Temperatures:</div>
                <div className="grid grid-cols-4 gap-2">
                  {systemInfo.cpu.core_temps.map((temp, i) => (
                    <div key={i} className="bg-gray-700 p-2 rounded">
                      Core {i}: {temp.toFixed(1)}°C
                    </div>
                  ))}
                </div>
              </div>
            )}
            */}
            {systemInfo.cpu?.flags && (
              <div className="mt-2">
                <div className="font-medium mb-1">CPU Features:</div>
                <div className="flex flex-wrap gap-1">
                  {systemInfo.cpu.flags.map((flag, i) => (
                    <span
                      key={i}
                      className="bg-gray-700 px-2 py-1 rounded text-xs"
                    >
                      {flag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );

    // Memory Information render function
    const renderMemoryInfo = () => (
      <div className="bg-gray-700 p-4 rounded">
        <h2 className="text-xl mb-4">Memory Information</h2>
        <div className="space-y-4">
          <div className="bg-gray-800 p-4 rounded">
            <div className="text-sm space-y-3">
              <div>
                RAM:{" "}
                {systemInfo.memory ? (
                  `${Math.round(
                    systemInfo.memory.capacity / (1024 * 1024 * 1024)
                  )} GB`
                ) : (
                  <span className="text-red-500">Not available</span>
                )}
              </div>
              {/*<div>
                Available memory: {systemInfo.memory ? 
                  `${Math.round(systemInfo.memory.availableCapacity / (1024 * 1024 * 1024))} GB` : 
                  <span className="text-red-500">Not available</span>}
              </div>
              <div>
                In use: {systemInfo.memory ? 
                  `${Math.round((systemInfo.memory.capacity - systemInfo.memory.availableCapacity) / (1024 * 1024 * 1024))} GB` : 
                  <span className="text-red-500">Not available</span>}
              </div>*/}
              <div>
                Memory type:{" "}
                {systemInfo.memory?.type || (
                  <span className="text-red-500">Not available</span>
                )}
              </div>
            </div>
          </div>

          {/* Módulos de memoria */}
          {systemInfo.memory?.modules &&
            systemInfo.memory.modules.length > 0 && (
              <div className="bg-gray-800 p-4 rounded">
                <h3 className="font-semibold mb-3">Memory Modules</h3>
                <div className="space-y-4">
                  {systemInfo.memory.modules.map((module, index) => (
                    <div
                      key={index}
                      className="text-sm space-y-2 border-l-2 border-blue-500 pl-3"
                    >
                      <div className="font-medium">
                        Module {index + 1} - {module.location}
                      </div>
                      <div>
                        Size: {Math.round(module.size / (1024 * 1024 * 1024))}{" "}
                        GB
                      </div>
                      {module.speed && <div>Speed: {module.speed} MHz</div>}
                      {module.manufacturer && (
                        <div>Manufacturer: {module.manufacturer}</div>
                      )}
                      {module.memory_type && (
                        <div>Type: {module.memory_type}</div>
                      )}
                      {module.form_factor && (
                        <div>Form Factor: {module.form_factor}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
        </div>
      </div>
    );

    // Network Information render function
    const renderNetworkInfo = () => (
      <div className="bg-gray-700 p-4 rounded">
        <h2 className="text-xl mb-4">Network Information</h2>
        <div className="space-y-4">
          {systemInfo.network_info ? (
            <>
              <div className="bg-gray-800 p-4 rounded">
                <h3 className="font-semibold mb-2">Total Traffic</h3>
                <div className="text-sm space-y-2">
                  <div>
                    Total Received:{" "}
                    {formatBytes(systemInfo.network_info.total_rx_bytes)}
                  </div>
                  <div>
                    Total Sent:{" "}
                    {formatBytes(systemInfo.network_info.total_tx_bytes)}
                  </div>
                </div>
              </div>

              {systemInfo.network_info.interfaces.map((iface, index) => (
                <div key={index} className="bg-gray-800 p-4 rounded">
                  <h3 className="font-semibold mb-2">{iface.name}</h3>
                  <div className="text-sm space-y-2">
                    <div>Status: {iface.status}</div>
                    <div>Received: {formatBytes(iface.rx_bytes)}</div>
                    <div>Sent: {formatBytes(iface.tx_bytes)}</div>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <div className="bg-gray-800 p-4 rounded">
              <span className="text-red-500">
                No network information available
              </span>
            </div>
          )}
        </div>
      </div>
    );

    // Disk Information render function
    const renderDiskInfo = () => (
      <div className="bg-gray-700 p-4 rounded">
        <h2 className="text-xl mb-4">Storage Information</h2>
        <div className="space-y-4">
          {systemInfo.storage ? (
            systemInfo.storage.map((disk, index) => (
              <div key={index} className="bg-gray-800 p-4 rounded">
                <h3 className="font-semibold mb-2">
                  {disk.name}
                  {disk.drive_letter && ` (${disk.drive_letter})`}
                  {/* Etiqueta de disco del sistema - Corregir condición */}
                  {systemDisk && disk === systemDisk ? (
                    <span className="text-blue-400 text-xs px-2 py-0.5 bg-blue-900/30 rounded ml-2">
                      System
                    </span>
                  ) : null}
                </h3>
                <div className="text-sm space-y-3">
                  <div>
                    Type:{" "}
                    {disk.type_name || disk.type || (
                      <span className="text-red-500">Not available</span>
                    )}
                  </div>
                  <div>
                    Total capacity:{" "}
                    {disk.capacity ? (
                      `${Math.round(disk.capacity / (1024 * 1024 * 1024))} GB`
                    ) : (
                      <span className="text-red-500">Not available</span>
                    )}
                  </div>
                  <div>
                    Free space:{" "}
                    {disk.available ? (
                      `${Math.round(disk.available / (1024 * 1024 * 1024))} GB`
                    ) : (
                      <span className="text-red-500">Not available</span>
                    )}
                  </div>
                  <div>
                    Used space:{" "}
                    {disk.capacity && disk.available ? (
                      `${Math.round(
                        (disk.capacity - disk.available) / (1024 * 1024 * 1024)
                      )} GB`
                    ) : (
                      <span className="text-red-500">Not available</span>
                    )}
                  </div>
                  <div>
                    Used percentage:{" "}
                    {disk.capacity && disk.available ? (
                      <span>
                        <span className={getUsageClass(((disk.capacity - disk.available) / disk.capacity) * 100)}>{Math.round(
                          ((disk.capacity - disk.available) / disk.capacity) * 10000
                        ) / 100}%</span>
                      </span>
                    ) : (
                      <span className="text-red-500">Not available</span>
                    )}
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2.5">
                    <div
                      className={`${getBarClass(
                        disk.capacity && disk.available
                          ? ((disk.capacity - disk.available) / disk.capacity) * 100
                          : 0
                      )} h-2.5 rounded-full`}
                      style={{
                        width: disk.capacity && disk.available
                          ? `${Math.round(((disk.capacity - disk.available) / disk.capacity) * 100)}%`
                          : "0%",
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="bg-gray-800 p-4 rounded">
              <span className="text-red-500">
                No storage information available
              </span>
            </div>
          )}
        </div>
      </div>
    );

    // GPU Information render function
    const renderGPUInfo = () => (
      <div className="bg-gray-700 p-4 rounded">
        <h2 className="text-xl mb-4">GPU Information</h2>
        <div className="space-y-4">
          {systemInfo.gpu_info && systemInfo.gpu_info.length > 0 ? (
            systemInfo.gpu_info.map((gpu, index) => (
              <div key={index} className="bg-gray-800 p-4 rounded">
                {/* Solo mostrar "GPU N:" cuando hay más de una GPU */}
                <h3 className="font-semibold mb-2">
                  {systemInfo.gpu_info && systemInfo.gpu_info.length > 1 ? `GPU ${index + 1}` : "GPU"}
                </h3>
                <div className="text-sm space-y-3">
                  <div>
                    Model:{" "}
                    {gpu.model || (
                      <span className="text-red-500">Not available</span>
                    )}
                  </div>
                  <div>
                    Dedicated memory:{" "}
                    {gpu.memory ? (
                      `${Math.round(gpu.memory / (1024 * 1024))} MB`
                    ) : (
                      <span className="text-red-500">Not available</span>
                    )}
                  </div>
                  <div>
                    Driver version:{" "}
                    {gpu.driver_version || (
                      <span className="text-red-500">Not available</span>
                    )}
                  </div>
                  {/*<div>
                    Temperature: {gpu.temperature ? 
                      `${gpu.temperature.toFixed(1)}°C` : 
                      <span className="text-red-500">Not available</span>}
                  </div>
                  <div>
                    Usage: {gpu.usage ? 
                      `${gpu.usage.toFixed(1)}%` : 
                      <span className="text-red-500">Not available</span>}
                  </div>
                  */}
                </div>
              </div>
            ))
          ) : (
            <div className="bg-gray-800 p-4 rounded">
              <span className="text-red-500">No GPU information available</span>
            </div>
          )}
        </div>
      </div>
    );

    const renderOSInfo = () => (
      <div className="bg-gray-700 p-4 rounded">
        <h2 className="text-xl mb-4">Operating System Information</h2>
        <div className="space-y-4">
          <div className="bg-gray-800 p-4 rounded">
            <div className="text-sm space-y-3">
              <div>
                OS Name:{" "}
                {systemInfo.os_info?.name || (
                  <span className="text-red-500">Not available</span>
                )}
              </div>
              <div>
                Version:{" "}
                {systemInfo.os_info?.version || (
                  <span className="text-red-500">Not available</span>
                )}
              </div>
              <div>
                Build:{" "}
                {systemInfo.os_info?.build || (
                  <span className="text-red-500">Not available</span>
                )}
              </div>
              <div>
                Architecture:{" "}
                {systemInfo.os_info?.architecture || (
                  <span className="text-red-500">Not available</span>
                )}
              </div>
              {systemInfo.os_info?.kernel_version && (
                <div>Kernel Version: {systemInfo.os_info.kernel_version}</div>
              )}
            </div>
          </div>

          <div className="bg-gray-800 p-4 rounded">
            <h3 className="font-semibold mb-2">System Details</h3>
            <div className="text-sm space-y-3">
              <div>
                System Drive:{" "}
                {systemInfo.os_info?.system_drive || (
                  <span className="text-red-500">Not available</span>
                )}
              </div>
              {/*<div>
                System Root:{" "}
                {systemInfo.os_info?.system_root || (
                  <span className="text-red-500">Not available</span>
                )}
              </div>*/}
              {systemInfo.os_info?.registered_owner && (
                <div>Registered Owner: {systemInfo.os_info.registered_owner}</div>
              )}
            </div>
          </div>

          {/* Añadir sección para firmware e información de compatibilidad */}
          {systemInfo.firmware_info && (
            <div className="bg-gray-800 p-4 rounded">
              <h3 className="font-semibold mb-2">Firmware & Security</h3>
              <div className="text-sm space-y-3">
                <div>
                  Boot Type: {systemInfo.firmware_info.is_uefi ? "UEFI" : "Legacy BIOS"}
                </div>
                {systemInfo.firmware_info.secure_boot_enabled !== undefined && (
                  <div>
                    Secure Boot: {systemInfo.firmware_info.secure_boot_enabled ? "Enabled" : "Disabled"}
                  </div>
                )}
                { /* Need admin rights to check TPM status
                <div>
                  TPM: {systemInfo.firmware_info.tpm_available ? 
                    (systemInfo.firmware_info.tpm_version ? 
                      `Available (${systemInfo.firmware_info.tpm_version})` : 
                      "Available") : 
                    "Not detected"}
                </div>
              */ }
              </div>
            </div>
          )}

          <div className="bg-gray-800 p-4 rounded">
            <h3 className="font-semibold mb-2">Time Information</h3>
            <div className="text-sm space-y-3">
              {systemInfo.os_info?.last_boot && (
                <div>Last Boot: {formatDate(systemInfo.os_info.last_boot)}</div>
              )}
              {systemInfo.os_info?.install_date && (
                <div>Install Date: {formatDate(systemInfo.os_info.install_date)}</div>
              )}
            </div>
          </div>
        </div>
      </div>
    );

    // Modificar el switch para manejar los menús laterales
    switch (activeTab) {
      case "system":
        if (selectedMenuItem === "cpu") return renderCPUInfo();
        if (selectedMenuItem === "memory") return renderMemoryInfo();
        if (selectedMenuItem === "disk") return renderDiskInfo();
        if (selectedMenuItem === "network") return renderNetworkInfo();
        if (selectedMenuItem === "gpu") return renderGPUInfo();
        // if (selectedMenuItem === 'sensors') return renderSensorsInfo();
        // ... resto del caso 'system' para el resumen general ...
        return (
          <div className="bg-gray-700 p-4 rounded">
            <h2 className="text-xl mb-4">System Information</h2>
            <div className="space-y-4">
              <div className="bg-gray-800 p-4 rounded">
                <h3 className="font-semibold mb-2">CPU</h3>
                <div className="text-sm space-y-2">
                  <div>
                    Model:{" "}
                    {!isLoading ? (
                      systemInfo.cpu?.modelName || (
                        <span className="text-red-500">Not available</span>
                      )
                    ) : (
                      ""
                    )}
                  </div>
                  <div>
                    Architecture:{" "}
                    {!isLoading ? (
                      systemInfo.cpu?.archName || (
                        <span className="text-red-500">Not available</span>
                      )
                    ) : (
                      ""
                    )}
                  </div>
                  <div>
                    Cores:{" "}
                    {!isLoading ? (
                      systemInfo.cpu?.cores && systemInfo.cpu?.threads ? (
                        `${systemInfo.cpu.cores} (${systemInfo.cpu.threads} threads)`
                      ) : systemInfo.cpu?.numOfProcessors || (
                        <span className="text-red-500">Not available</span>
                      )
                    ) : (
                      ""
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-gray-800 p-4 rounded">
                <h3 className="font-semibold mb-2">Memory</h3>
                <div className="text-sm space-y-2">
                  <div>
                    RAM:{" "}
                    {!isLoading ? (
                      systemInfo.memory ? (
                        `${Math.round(
                          systemInfo.memory.capacity / (1024 * 1024 * 1024)
                        )} GB`
                      ) : (
                        <span className="text-red-500">Not available</span>
                      )
                    ) : (
                      ""
                    )}
                  </div>
                  <div>
                    Memory type:{" "}
                    {!isLoading ? (
                      systemInfo.memory?.type || (
                        <span className="text-red-500">Not available</span>
                      )
                    ) : (
                      ""
                    )}
                  </div>
                </div>
              </div>

              {/* GPU Summary */}
              <div className="bg-gray-800 p-4 rounded">
                <h3 className="font-semibold mb-2">GPUs</h3>
                <div className="text-sm space-y-2">
                  {!isLoading ? (
                    systemInfo.gpu_info && systemInfo.gpu_info.length > 0 ? (
                      systemInfo.gpu_info.map((gpu, index) => (
                        <div key={index} className="space-y-1">
                          <div>
                            {/* Solo mostrar el número de GPU cuando hay más de una */}
                            {systemInfo.gpu_info && systemInfo.gpu_info.length > 1 ?
                              `GPU ${index + 1}: ` :
                              ""
                            }
                            {gpu.model || (
                              <span className="text-red-500">Not available</span>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <span className="text-red-500">
                        No GPU information available
                      </span>
                    )
                  ) : (
                    ""
                  )}
                </div>
              </div>

              {/* Storage Summary */}
              <div className="bg-gray-800 p-4 rounded">
                <h3 className="font-semibold mb-2">Storage</h3>
                <div className="text-sm space-y-2">
                  {!isLoading ? (
                    systemInfo.storage ? (
                      systemInfo.storage.map((disk, index) => (
                        <div key={index} className="space-y-1">
                          <div className="flex justify-between items-center">
                            <span className="truncate">
                              {disk.name}
                              {disk.drive_letter && ` (${disk.drive_letter})`}
                              {/* Corregida la condición para mostrar etiqueta de disco del sistema */}
                              {systemDisk && disk === systemDisk ? (
                                <span className="text-blue-400 text-xs px-2 py-0.5 bg-blue-900/30 rounded ml-2">
                                  System
                                </span>
                              ) : null}
                            </span>
                            <span className="text-gray-400 ml-2">
                              {disk.capacity
                                ? `${Math.round(disk.capacity / (1024 * 1024 * 1024))} GB`
                                : 'N/A'}
                            </span>
                          </div>
                          <div className="w-full bg-gray-700 rounded-full h-2.5">
                            <div
                              className={`${getBarClass(
                                disk.capacity && disk.available
                                  ? ((disk.capacity - disk.available) / disk.capacity) * 100
                                  : 0
                              )} h-2.5 rounded-full`}
                              style={{
                                width: disk.capacity && disk.available
                                  ? `${Math.round(((disk.capacity - disk.available) / disk.capacity) * 100)}%`
                                  : "0%",
                              }}
                            ></div>
                          </div>
                          <div className="text-xs text-gray-400">
                            {disk.available
                              ? `${Math.round(
                                disk.available / (1024 * 1024 * 1024)
                              )} GB free`
                              : ""}
                          </div>
                        </div>
                      ))
                    ) : (
                      <span className="text-red-500">
                        No storage information available
                      </span>
                    )
                  ) : (
                    ""
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      case "peripherals":
        return (
          <div className="bg-gray-700 p-4 rounded">
            <h2 className="text-xl mb-4">Peripherals</h2>
            <div className="space-y-4">
              {/* 1. Display Devices - Ahora primero */}
              <div className="bg-gray-800 p-4 rounded">
                <h3 className="font-semibold mb-2">Display Devices</h3>
                <div className="text-sm">
                  {/* Versión simplificada que muestra la información de pantalla en una línea */}
                  {systemInfo.display && systemInfo.display.length > 0 ? (
                    systemInfo.display.map((display, index) => (
                      <div key={index} className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center">
                          <span className="font-medium mr-2">
                            {display.name || `Display ${index + 1}`}
                          </span>
                          {display.primary && (
                            <span className="text-blue-400 text-xs px-2 py-0.5 bg-blue-900/30 rounded">
                              Primary
                            </span>
                          )}
                        </div>
                        <div className="text-gray-400 flex gap-3">
                          <span>{display.resolution.width} x {display.resolution.height}</span>
                          <span>{display.refresh_rate || 60} Hz</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <span className="text-red-500">
                      Not available
                    </span>
                  )}
                </div>
              </div>
              {/* 2. Audio Devices - Ahora segundo */}
              <div className="bg-gray-800 p-4 rounded">
                <h3 className="font-semibold mb-2">Audio Devices</h3>
                <div className="text-sm space-y-2">
                  {systemInfo.peripherals?.audio_devices ? (
                    <>
                      <div className="mb-3">
                        <div className="font-medium text-gray-400 mb-2">
                          Playback Devices
                        </div>
                        {systemInfo.peripherals.audio_devices
                          .filter((device) => device.type === "playback")
                          .map((device, index) => (
                            <div
                              key={index}
                              className="flex justify-between items-center mb-1"
                            >
                              <div>
                                {device.name}
                                {device.default && (
                                  <span className="text-green-500 ml-2">
                                    (Default)
                                  </span>
                                )}
                              </div>
                              <span
                                className={`text-xs px-2 py-0.5 rounded ${device.active ? "bg-green-600" : "bg-gray-600"
                                  }`}
                              >
                                {device.active ? "Active" : "Inactive"}
                              </span>
                            </div>
                          ))}
                      </div>
                      <div>
                        <div className="font-medium text-gray-400 mb-2">
                          Recording Devices
                        </div>
                        {systemInfo.peripherals.audio_devices
                          .filter((device) => device.type === "recording")
                          .map((device, index) => (
                            <div
                              key={index}
                              className="flex justify-between items-center mb-1"
                            >
                              <div>
                                {device.name}
                                {device.default && (
                                  <span className="text-green-500 ml-2">
                                    (Default)
                                  </span>
                                )}
                              </div>
                              <span
                                className={`text-xs px-2 py-0.5 rounded ${device.active ? "bg-green-600" : "bg-gray-600"
                                  }`}
                              >
                                {device.active ? "Active" : "Inactive"}
                              </span>
                            </div>
                          ))}
                      </div>
                    </>
                  ) : (
                    <span className="text-red-500">
                      No audio devices available
                    </span>
                  )}
                </div>
              </div>

              {/* 3. Input Devices - Ahora tercero */}
              <div className="bg-gray-800 p-4 rounded">
                <h3 className="font-semibold mb-2">Input Devices</h3>
                <div className="text-sm space-y-2">
                  {systemInfo.peripherals?.input_devices ? (
                    systemInfo.peripherals.input_devices.map(
                      (device, index) => (
                        <div
                          key={index}
                          className="flex justify-between items-center"
                        >
                          <div>
                            <span className="text-gray-400">
                              {device.type}:{" "}
                            </span>
                            {device.name}
                            {device.manufacturer && (
                              <span className="text-gray-400 ml-2">
                                ({device.manufacturer})
                              </span>
                            )}
                          </div>
                          <span
                            className={`text-xs px-2 py-0.5 rounded ${device.connected ? "bg-green-600" : "bg-gray-600"
                              }`}
                          >
                            {device.connected ? "Connected" : "Disconnected"}
                          </span>
                        </div>
                      )
                    )
                  ) : (
                    <span className="text-red-500">
                      No input devices available
                    </span>
                  )}
                </div>
              </div>

              {/* 4. Printers - Ahora último */}
              <div className="bg-gray-800 p-4 rounded">
                <h3 className="font-semibold mb-2">Printers</h3>
                <div className="text-sm space-y-2">
                  {systemInfo.peripherals?.printers ? (
                    systemInfo.peripherals.printers.map((printer, index) => (
                      <div key={index} className="bg-gray-700 p-3 rounded">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="font-medium flex items-center gap-2">
                              {printer.name}
                              {printer.is_default && (
                                <span className="text-green-500 text-xs">
                                  (Default)
                                </span>
                              )}
                            </div>
                            {printer.manufacturer && (
                              <div className="text-gray-400 text-xs">
                                {printer.manufacturer}
                                {printer.model && ` - ${printer.model}`}
                              </div>
                            )}
                          </div>
                          <div
                            className={`text-xs px-2 py-0.5 rounded ${printer.status.toLowerCase() === "ready"
                                ? "bg-green-600"
                                : "bg-yellow-600"
                              }`}
                          >
                            {printer.status}
                          </div>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {printer.color && (
                            <span className="text-xs bg-gray-600 px-2 py-1 rounded">
                              Color
                            </span>
                          )}
                          {printer.duplex && (
                            <span className="text-xs bg-gray-600 px-2 py-1 rounded">
                              Duplex
                            </span>
                          )}
                          {printer.is_shared && (
                            <span className="text-xs bg-gray-600 px-2 py-1 rounded">
                              Shared
                            </span>
                          )}
                          {printer.location && (
                            <span className="text-xs bg-gray-600 px-2 py-1 rounded">
                              {printer.location}
                            </span>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <span className="text-red-500">No printers available</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      case "os":
        return renderOSInfo();
      case "vitals":
        return (
          <div className="bg-gray-700 p-4 rounded space-y-4">
            <h2 className="text-xl mb-4">System Vitals</h2>

            {/* CPU Usage */}
            <div className="bg-gray-800 p-4 rounded">
              <div className="flex justify-between mb-2">
                <span>CPU Usage</span>
                <span>
                  {(systemInfo.cpu?.usage ?? 0).toFixed(1)}%
                </span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-2.5">
                <div
                  className="bg-green-600 h-2.5 rounded-full transition-all duration-500"
                  style={{ width: `${systemInfo.cpu?.usage ?? 0}%` }}
                ></div>
              </div>
            </div>

            {/* Memory Usage */}
            <div className="bg-gray-800 p-4 rounded">
              <div className="flex justify-between mb-2">
                <span>Memory Usage</span>
                <span>
                  {systemInfo.memory ?
                    `${((systemInfo.memory.capacity - systemInfo.memory.availableCapacity) / (1024 * 1024 * 1024)).toFixed(1)} GB / 
                     ${(systemInfo.memory.capacity / (1024 * 1024 * 1024)).toFixed(1)} GB 
                     (${systemInfo.memory.usedPercentage.toFixed(1)}%)` :
                    'N/A'}
                </span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-2.5">
                <div
                  className={`${getBarClass(systemInfo.memory?.usedPercentage ?? 0)} h-2.5 rounded-full transition-all duration-500`}
                  style={{ width: `${systemInfo.memory?.usedPercentage || 0}%` }}
                ></div>
              </div>
            </div>

            {/* System Stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-800 p-4 rounded">
                <h3 className="text-lg mb-2">System Load</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Running Processes:</span>
                    <span>{systemInfo.processes?.total || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Active Services:</span>
                    <span>{systemInfo.services?.running || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>System Uptime:</span>
                    <span>{systemInfo.os_info ? formatUptime(systemInfo.os_info.uptime) : 'N/A'}</span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-800 p-4 rounded">
                <h3 className="text-lg mb-2">Network Activity</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Total Received:</span>
                    <span>{systemInfo.network_info ? formatBytes(systemInfo.network_info.total_rx_bytes) : 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Total Sent:</span>
                    <span>{systemInfo.network_info ? formatBytes(systemInfo.network_info.total_tx_bytes) : 'N/A'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  // Función auxiliar para formatear bytes
  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  };

  // Función auxiliar para formatear el tiempo de uptime
  const formatUptime = (seconds?: number): string => {
    if (!seconds) return "Not available";

    const days = Math.floor(seconds / (24 * 60 * 60));
    const hours = Math.floor((seconds % (24 * 60 * 60)) / (60 * 60));
    const minutes = Math.floor((seconds % (60 * 60)) / 60);

    const parts = [];
    if (days > 0) parts.push(`${days} day${days !== 1 ? 's' : ''}`);
    if (hours > 0) parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
    if (minutes > 0) parts.push(`${minutes} minute${minutes !== 1 ? 's' : ''}`);

    return parts.length > 0 ? parts.join(', ') : "Just started";
  };

  // Función auxiliar para formatear fechas de WMI
  const formatDate = (dateString: string): string => {
    // El formato es: YYYYMMDDHHMMSS.mmmmmm±UUU
    // donde UUU es el offset en minutos
    const year = parseInt(dateString.substring(0, 4));
    const month = parseInt(dateString.substring(4, 6)) - 1; // Los meses en JS van de 0-11
    const day = parseInt(dateString.substring(6, 8));
    const hour = parseInt(dateString.substring(8, 10));
    const minute = parseInt(dateString.substring(10, 12));
    const second = parseInt(dateString.substring(12, 14));

    const offsetSign = dateString.charAt(21);
    const offsetMinutes = parseInt(dateString.substring(22, 25));

    // Crear fecha en UTC
    const date = new Date(Date.UTC(year, month, day, hour, minute, second));

    // Ajustar por el offset
    const offsetMs = offsetMinutes * 60 * 1000 * (offsetSign === '+' ? 1 : -1);
    date.setTime(date.getTime() + offsetMs);

    // Formatear la fecha para mostrar
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getInstallerLink = () => {
    const platform = window.navigator.platform.toLowerCase();
    if (platform.includes('win')) {
      return '/downloads/pchardware-monitor-installer.exe';
    } else if (platform.includes('linux')) {
      return '/downloads/pchardware-monitor-installer';
    } else if (platform.includes('mac')) {
      return '/downloads/pchardware-monitor-installer.dmg';
    }
    return '/downloads/pchardware-monitor-installer'; // fallback a linux
  };

  const getInstallerText = () => {
    const platform = window.navigator.platform.toLowerCase();
    if (platform.includes('win')) {
      return 'Download Windows Installer';
    } else if (platform.includes('linux')) {
      return 'Download Linux Installer';
    } else if (platform.includes('mac')) {
      return 'Download macOS Installer';
    }
    return 'Download Installer';
  };

  // Banner modificado para mostrar ambos siempre al inicio
  const ExtensionBanner = () => {
    const banners = [];
    const browserName = getBrowserName();

    // Siempre mostramos el banner de la extensión si no está instalada
    if (showExtensionBanner && !extensionBannerClosed) {
      banners.push(
        <div
          key="extension"
          className="fixed top-0 left-0 right-0 z-50 bg-blue-600 text-white px-4 py-1 flex items-center justify-between"
        >
          <div className="flex-1 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            <span>
              To view your system basic details, you'll need a {browserName} extension.
            </span>
          </div>
          <div className="flex items-center gap-4">
            <a
              href={getExtensionLink()}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-blue-600 px-4 py-1 rounded-md text-sm font-medium hover:bg-blue-50 transition-colors"
            >
              Install Extension for {browserName}
            </a>
            <button
              onClick={() => {
                setShowExtensionBanner(false);
                setExtensionBannerClosed(true);
              }}
              className="text-white hover:text-blue-100 transition-colors"
              aria-label="Close banner"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      );
    }

    // Siempre mostramos el banner del programa Rust
    if (showRustBanner && !rustBannerClosed) {
      banners.push(
        <div
          key="rust"
          className="fixed top-0 left-0 right-0 z-40 bg-amber-600 text-white px-4 py-1 flex items-center justify-between"
          style={{ marginTop: showExtensionBanner && !extensionBannerClosed ? "36px" : "0px" }}
        >
          <div className="flex-1 flex items-center gap-2">
            <Download className="w-5 h-5" />
            <span>
              For more extended info you'll need to install a helper for
              the extension.
            </span>
          </div>
          <div className="flex items-center gap-4">
            <a
              href={getInstallerLink()}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-amber-600 px-4 py-1 rounded-md text-sm font-medium hover:bg-amber-50 transition-colors"
            >
              {getInstallerText()}
            </a>
            <button
              onClick={() => {
                setShowRustBanner(false);
                setRustBannerClosed(true);
                setAgentReady(true); // Added to hide the spinner after closing the banner
              }}
              className="text-white hover:text-amber-100 transition-colors"
              aria-label="Close banner"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      );
    }

    return createPortal(<>{banners}</>, document.body);
  };

  const UpdateBanner = () => {
    if (!updateNotification.show || updateBannerClosed) return null;

    return createPortal(
      <div className="fixed top-0 left-0 right-0 z-60 bg-yellow-600 text-white px-4 py-1 flex items-center justify-between">
        <div className="flex-1 flex items-center gap-2">
          <RefreshCw className="w-5 h-5" />
          <span>
            A new version ({updateNotification.latestVersion}) of the Agent is available.
            Your current version is {updateNotification.currentVersion}.
          </span>
        </div>
        <div className="flex items-center gap-4">
          <a
            href={getInstallerLink()}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white text-yellow-600 px-4 py-1 rounded-md text-sm font-medium hover:bg-yellow-50 transition-colors"
          >
            Download Update
          </a>
          <button
            onClick={() => {
              setUpdateNotification(prev => ({ ...prev, show: false }));
              setUpdateBannerClosed(true);
            }}
            className="text-white hover:text-yellow-100 transition-colors"
            aria-label="Close banner"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>,
      document.body
    );
  };

  return (
    <div className="min-h-[99vh] bg-gray-900">
      {!isLoading && (
        <>
          <UpdateBanner />
          <ExtensionBanner />
        </>
      )}

      {/* Contenedor principal siempre visible - eliminado padding condicional */}
      <div className="container mx-auto px-4 py-8 min-w-[768px] overflow-auto">
        <div className="bg-gray-800 text-white p-4 rounded-lg w-full max-w-4xl mx-auto min-w-[728px]">
          {/* Header con menú */}
          <div className="flex justify-between items-center mb-4 flex-wrap sm:flex-nowrap">
            <div className="flex items-center gap-4 flex-wrap sm:flex-nowrap">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-green-500 rounded"></div>
                <span>CPU:</span>
                <span>{(systemInfo.cpu?.usage ?? 0).toFixed(1)}%</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-blue-500 rounded"></div>
                <span>RAM:</span>
                <span className={getUsageClass(systemInfo.memory?.usedPercentage ?? 0)}>
                  {(systemInfo.memory?.usedPercentage ?? 0).toFixed(1)}%
                </span>
              </div>
              {/* Mostrar el uso del disco del sistema de manera condicional pero visible */}
              {systemDisk && systemDisk.capacity && systemDisk.available && (
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-purple-500 rounded"></div>
                  <span>System Disk{systemDisk.drive_letter ? ` (${systemDisk.drive_letter})` : ''}:</span>
                  <span className={getUsageClass(systemDiskUsage)}>
                    {systemDiskUsage.toFixed(1)}%
                  </span>
                </div>
              )}
            </div>
            <div>
              <div className="text-lg font-semibold">my.PcHardware.info</div>
              {systemInfo.nativeAgent && (
                <div className="text-xs text-gray-400 text-right flex items-center justify-end">
                  {systemInfo.nativeAgent.version
                    ? `Helper v${systemInfo.nativeAgent.version}`
                    : (!isLoading && !agentReady && !showRustBanner) ? (
                      <div className="flex items-center">
                        <div className="relative w-24 h-2 bg-gray-600 rounded mr-1">
                          <div
                            className="absolute top-0 left-0 h-2 bg-blue-500 rounded"
                            style={{ width: `${progressValue}%`, transition: 'width 0.05s linear' }}>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <span className="text-red-500">Helper not available </span>
                    )
                  } | App v{config.appVersion}
                </div>
              )}
            </div>
            <div className="flex items-center gap-4">
              <button
                className="relative"
                onClick={toggleMenu}
                ref={menuButtonRef} // Añadimos la referencia aquí
              >
                <MenuIcon className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Overlay de carga */}
          {isLoading && (
            <div className="absolute inset-0 bg-gray-900/50 flex flex-col items-center justify-center z-50">
              <Loader2 className="w-16 h-16 animate-spin text-blue-500" />
              <p className="text-gray-400 mt-4 text-lg">Loading basic information...</p>
            </div>
          )}

          {/* Show menu panel when showMenu is true */}
          {showMenu && (
            <MemoizedMenuPanel
              buttonRect={menuButtonRef.current?.getBoundingClientRect()}
              onGeneratePDF={generatePDF}
              onClose={closeMenu}
              menuRef={menuRef}
            />
          )}

          {/* Navigation - Modificar los onClick para usar la nueva función */}
          <div className="flex gap-2 mb-4 bg-gray-700 p-2 rounded overflow-x-auto">
            <button
              onClick={() => handleTabClick("system")}
              className={`flex flex-col items-center p-2 rounded ${activeTab === "system" ? "bg-gray-600" : ""
                }`}
            >
              <PcCase className="w-6 h-6" />
              <span>System</span>
            </button>

            <button
              onClick={() => handleTabClick("peripherals")}
              className={`flex flex-col items-center p-2 rounded ${activeTab === "peripherals" ? "bg-gray-600" : ""
                }`}
            >
              <Monitor className="w-6 h-6" />
              <span>Peripherals</span>
            </button>
            <button
              onClick={() => handleTabClick("os")}
              className={`flex flex-col items-center p-2 rounded ${activeTab === "os" ? "bg-gray-600" : ""
                }`}
            >
              <AppWindow className="w-6 h-6" />
              <span>&nbsp;&nbsp;&nbsp;OS&nbsp;&nbsp;&nbsp;</span>
            </button>
            <button
              onClick={() => handleTabClick("vitals")}
              className={`flex flex-col items-center p-2 rounded ${activeTab === "vitals" ? "bg-gray-600" : ""
                }`}
            >
              <Activity className="w-6 h-6" />
              <span>Vitals</span>
            </button>
          </div>

          {/* Main Content Area with Left Sidebar */}
          <div className="flex gap-4 flex-col sm:flex-row">
            {/* Left Sidebar - Solo mostrar si activeTab es 'system' */}
            {activeTab === "system" && (
              <SideMenu
                selectedMenuItem={selectedMenuItem}
                onMenuItemClick={handleMenuItemClick}
              />
            )}

            {/* Main Content - Ajustar el ancho cuando no hay sidebar */}
            <div className={`${activeTab === "system" ? "flex-1" : "w-full"}`}>
              <TabContent />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemMonitor;
